import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';
import VenueSelector from '../components/VenueSelector';
import TeamNavbar from '../components/TeamNavbar';

const VenueDiscovery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [filters, setFilters] = useState({
    date: '',
    difficulty: 'all',
    categories: [],
    maxDistance: 25 // miles
  });

  // Available categories for filtering
  const availableCategories = [
    'History', 'Science', 'Geography', 'Sports', 'Entertainment', 
    'Literature', 'Music', 'Movies & TV', 'Technology', 'Art', 
    'Politics', 'Business', 'Food & Drink', 'Nature', 'Math',
    'Pop Culture', 'Mythology', 'Religion', 'Fashion', 'Other'
  ];

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    setLoading(true);
    try {
      // Get upcoming games from the correct collection
      const now = new Date();
      const gamesQuery = query(
        collection(db, 'scheduledGames'),
        orderBy('scheduledDateTime', 'asc'),
        limit(100)
      );
      
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesData = [];
      
      gamesSnapshot.forEach(doc => {
        const data = doc.data();
        const gameDateTime = data.scheduledDateTime?.toDate?.() || new Date(data.scheduledDateTime);
        
        // Client-side filtering for status and date
        if (data.status === 'scheduled' && gameDateTime > now) {
          gamesData.push({
            id: doc.id,
            ...data,
            scheduledDateTime: gameDateTime
          });
        }
      });
      
      setGames(gamesData);
      setFilteredGames(gamesData);
    } catch (error) {
      console.error('Error loading games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (venue) => {
    setSelectedLocation(venue);
    filterGames(venue);
  };

  const filterGames = (location = selectedLocation) => {
    if (!location) {
      setFilteredGames(games);
      return;
    }

    let filtered = games.filter(game => {
      // Filter by date
      if (filters.date) {
        const gameDate = game.scheduledDateTime.toISOString().split('T')[0];
        if (gameDate !== filters.date) return false;
      }

      // Filter by difficulty
      if (filters.difficulty !== 'all' && game.gameDifficulty !== filters.difficulty) {
        return false;
      }

      // Filter by categories
      if (filters.categories.length > 0) {
        const hasMatchingCategory = filters.categories.some(cat => 
          game.gameCategories?.includes(cat)
        );
        if (!hasMatchingCategory) return false;
      }

      // Filter by distance (if location is selected)
      if (location && game.venueLocation) {
        const distance = calculateDistance(
          location.location.lat,
          location.location.lng,
          game.venueLocation.lat,
          game.venueLocation.lng
        );
        if (distance > filters.maxDistance) return false;
      }

      return true;
    });

    setFilteredGames(filtered);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters };
    
    if (filterType === 'categories') {
      newFilters.categories = newFilters.categories.includes(value)
        ? newFilters.categories.filter(c => c !== value)
        : [...newFilters.categories, value];
    } else {
      newFilters[filterType] = value;
    }
    
    setFilters(newFilters);
    filterGames();
  };

  const formatDateTime = (dateTime) => {
    return dateTime.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'hard': return 'text-orange-400';
      case 'expert': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Team Navigation Bar */}
      <TeamNavbar />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Logo variant="white" size={64} />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Find Trivia Games</h1>
            <p className="text-gray-400">Discover trivia games near you</p>
          </div>

          {/* Search and Filters */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Location Search */}
              <div>
                <label className="block text-white mb-2">Search by Location</label>
                <VenueSelector
                  onVenueSelect={handleLocationSelect}
                  selectedVenue={selectedLocation}
                  placeholder="Enter your city or zip code..."
                />
              </div>

              {/* Filters */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white mb-2">Date</label>
                    <input
                      type="date"
                      value={filters.date}
                      onChange={(e) => handleFilterChange('date', e.target.value)}
                      className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-white mb-2">Max Distance (miles)</label>
                    <select
                      value={filters.maxDistance}
                      onChange={(e) => handleFilterChange('maxDistance', parseInt(e.target.value))}
                      className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                    >
                      <option value={5}>5 miles</option>
                      <option value={10}>10 miles</option>
                      <option value={25}>25 miles</option>
                      <option value={50}>50 miles</option>
                      <option value={100}>100 miles</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white mb-2">Difficulty</label>
                  <select
                    value={filters.difficulty}
                    onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                  >
                    <option value="all">All Difficulties</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white mb-2">Categories</label>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {availableCategories.map((category) => (
                      <label key={category} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={filters.categories.includes(category)}
                          onChange={() => handleFilterChange('categories', category)}
                          className="rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-300">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                <p className="text-white">Loading games...</p>
              </div>
            ) : filteredGames.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold text-white mb-2">No Games Found</h3>
                <p className="text-gray-400">
                  {games.length === 0 
                    ? "No upcoming trivia games are currently scheduled. Check back later!"
                    : "No games match your current filters. Try adjusting your search criteria."
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">
                    Found {filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''}
                  </h2>
                  <button
                    onClick={() => {
                      setSelectedLocation(null);
                      setFilters({
                        date: '',
                        difficulty: 'all',
                        categories: [],
                        maxDistance: 25
                      });
                      setFilteredGames(games);
                    }}
                    className="text-green-500 hover:text-green-400 text-sm"
                  >
                    Clear Filters
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredGames.map((game) => (
                    <div key={game.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colors">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-bold text-white">{game.gameTitle}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(game.gameDifficulty)}`}>
                          {game.gameDifficulty}
                        </span>
                      </div>

                      <p className="text-gray-400 text-sm mb-4">{game.gameDescription}</p>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm">
                          <span className="text-gray-500 w-16">Venue:</span>
                          <span className="text-white">{game.venueName}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="text-gray-500 w-16">Date:</span>
                          <span className="text-white">{formatDateTime(game.scheduledDateTime)}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="text-gray-500 w-16">Host:</span>
                          <span className="text-white">{game.hostName}</span>
                        </div>
                        {game.entryFee > 0 && (
                          <div className="flex items-center text-sm">
                            <span className="text-gray-500 w-16">Entry:</span>
                            <span className="text-green-400">${game.entryFee}</span>
                          </div>
                        )}
                      </div>

                      {game.gameCategories && game.gameCategories.length > 0 && (
                        <div className="mb-4">
                          <div className="text-sm text-gray-500 mb-2">Categories:</div>
                          <div className="flex flex-wrap gap-1">
                            {game.gameCategories.slice(0, 3).map((category) => (
                              <span
                                key={category}
                                className="px-2 py-1 bg-gray-700 text-xs text-gray-300 rounded"
                              >
                                {category}
                              </span>
                            ))}
                            {game.gameCategories.length > 3 && (
                              <span className="px-2 py-1 bg-gray-700 text-xs text-gray-300 rounded">
                                +{game.gameCategories.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-400">
                          {game.teams?.length || 0} teams registered
                        </div>
                        <button
                          onClick={() => navigate(`/launch-game/${game.id}`)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold transition-colors"
                        >
                          Join Game
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueDiscovery; 